import { useState } from 'react';
import { Button, Flex, InputNumber, Popconfirm, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { useVinExtractionService } from '../../services/vinExtractionService';
import { spacing } from '../../theming';
import { cardStyle, labelStyle } from './vinExtractionStyles';
import { isLostAnswer, notifyVinError } from './vinExtractionErrors';

// small by default: a test run is meant to check a model or a level, not to work through the backlog
const DEFAULT_TEST_CARS = 20;

/**
 * After a lost answer the batch may have been queued anyway (the gateway cuts the request, the API carries on), so
 * the fresh in-flight count decides: a running batch is reported as such, otherwise "check Batches first" (SC-QA-40).
 */
const notifyLostAnswer = (t, fresh) => {
    if (fresh?.inFlightBatches > 0) {
        globalNotification.open({
            type: notificationType.WARNING,
            message: t('edit.testBatchAnswerLost'),
            placement: 'top',
        });
    } else {
        globalNotification.open({
            type: notificationType.ERROR,
            message: t('errors.TEST_BATCH_NO_ANSWER'),
            placement: 'top',
        });
    }
};

/** Run → POST one batch, then re-read the settings either way, so the state and in-flight count show the truth. */
const useTestBatchRun = ({ onReload }) => {
    const { t } = useTranslation('vinExtraction');
    const { runTestBatch } = useVinExtractionService();
    const [running, setRunning] = useState(false);

    const run = async carLimit => {
        setRunning(true);
        try {
            // the batch is queued once the call answers, so an unexpected body must not read as a failure
            const { jobId, cars } = (await runTestBatch(carLimit)) ?? {};
            globalNotification.open({
                type: notificationType.SUCCESS,
                message: t('edit.testBatchQueued', { jobId: jobId ?? '—', cars: cars ?? carLimit }),
                placement: 'top',
            });
        } catch (error) {
            if (isLostAnswer(error)) {
                notifyLostAnswer(t, await onReload());
                setRunning(false);
                return;
            }
            notifyVinError(t, error);
        }
        await onReload();
        setRunning(false);
    };

    return { running, run };
};

/**
 * One batch with the saved settings, whatever the Processing switch says (to try a model or a thinking level).
 * Locked while the environment kill switch is off and in a read-only session; the API refuses the rest (409).
 */
export default function TestBatchCard({ settings, readOnly, onReload }) {
    const { t } = useTranslation('vinExtraction');
    const { min, max } = settings.bounds.batchCarLimit;
    const [carLimit, setCarLimit] = useState(Math.min(Math.max(DEFAULT_TEST_CARS, min), max));
    const { running, run } = useTestBatchRun({ onReload });
    const envOff = settings.enabledByEnv === false;
    const locked = envOff || readOnly;

    return (
        <Flex vertical gap={spacing[1]} style={cardStyle}>
            <Typography.Text strong>{t('edit.testBatch')}</Typography.Text>
            <span style={labelStyle}>{envOff ? t('edit.testBatchEnvOff') : t('edit.testBatchHelp')}</span>
            <Flex align="center" gap={spacing[2]}>
                <InputNumber
                    aria-label={t('edit.testBatchCars')}
                    addonBefore={t('edit.testBatchCars')}
                    min={min}
                    max={max}
                    step={1}
                    precision={0}
                    value={carLimit}
                    onChange={setCarLimit}
                    disabled={locked}
                />
                <Popconfirm
                    title={t('edit.testBatchConfirm', { cars: carLimit })}
                    okText={t('edit.confirmOk')}
                    cancelText={t('edit.confirmCancel')}
                    onConfirm={() => run(carLimit)}
                    disabled={locked || running || !carLimit}
                >
                    <Button loading={running} disabled={locked || !carLimit}>
                        {t('edit.testBatchRun')}
                    </Button>
                </Popconfirm>
            </Flex>
        </Flex>
    );
}
